import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Button from '../../../components/Buttons/Button';
import DataTable from '../../../components/DataTable/DataTable';
import ButtonBar from '../../../components/Fields/ButtonBar';
import MultiSelectField from '../../../components/Fields/MultiSelectField';
import { FieldConfig, FormConfig } from '../../../components/Fields/Types';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../../components/Spinner/SpinnerOverlay';
import { useNotifications } from '../../../contexts/notificationContext';
import ProductCategoryEmployeeModel from '../../../models/ProductCategoryEmployeeModel';
import DataService from "../../../services/data.service";
import { Result } from '../../../types/Result';

interface Props {
    fromEmployeeId: number
    functionalRoleId?: number
    toEmployeeId?: number
}

interface EmployeeMaintenanceProductCategoryModel {
    fromEmployeeId: number
    toEmployeeId?: number
    functionalRoleIds: number[]
    productCategoryEmployeeIds: number[]
}

const EmployeeMaintenance_tab_ProductCategories: React.FC<Props> = ({ fromEmployeeId, functionalRoleId, toEmployeeId }) => {
    const [showSpinner, setShowSpinner] = useState(false)
    const [tableData, setTableData] = useState<ProductCategoryEmployeeModel[]>([]);
    const [formData, setFormData] = useState<EmployeeMaintenanceProductCategoryModel>({ fromEmployeeId: fromEmployeeId, toEmployeeId: toEmployeeId, functionalRoleIds: [], productCategoryEmployeeIds: [] })
    const { handleResult, handleException } = useNotifications();

    const functionalRoles = useSelectListOptions(`api/v1/selectlist/FunctionalRolesForEmployeeMaintenanceProductCategories?fromEmployeeId=${fromEmployeeId}&toEmployeeId=${toEmployeeId}`, '-- Select workflow role');

    useEffect(() => {
        formData.fromEmployeeId = fromEmployeeId
        formData.toEmployeeId = toEmployeeId
        if (functionalRoleId)
            formData.functionalRoleIds = [functionalRoleId]
        else
            formData.functionalRoleIds = []

        setFormData(x => ({ ...formData }));
        getTableData(formData);
    }, [fromEmployeeId, toEmployeeId, functionalRoleId]);

    useEffect(() => {
        getTableData(formData);
    }, [formData.fromEmployeeId, formData.toEmployeeId, formData.functionalRoleIds]);

    function getTableData(form) {
        if (fromEmployeeId) {
            setShowSpinner(true)
            DataService.post<ProductCategoryEmployeeModel[]>(`api/v1/ProductCategoryEmployees/ForEmployeeMaintenance`, form)
                .then(response => {
                    setShowSpinner(false)
                    setTableData(response)
                })
                .catch(e => {
                    setShowSpinner(false)
                    handleException(e)
                });
        }
    }

    function submit() {
        if (tableData) {
            formData.productCategoryEmployeeIds = tableData.filter(x => x['_checkbox_selected'] === true).map(x => x.id)
            setShowSpinner(true)
            DataService.post<Result>(`api/v1/ProductCategoryEmployees/ChangeProductCategoryEmployees`, formData)
                .then(response => {
                    setShowSpinner(false)
                    handleResult(response)
                })
                .catch(e => {
                    setShowSpinner(false)
                    handleException(e)
                });
        }
    }

    function setFieldValue(name: string, value: string) {
        if (formData) {
            name.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData(formData);
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData);
        }
    }

    function onCheckboxButtonClick(selectedRows: ProductCategoryEmployeeModel[]) {
        if (tableData) {
            const select = tableData.length !== selectedRows.length
            tableData.forEach(x => x['_checkbox_selected'] = select);
            setTableData([...tableData])
        }
    }

    let fields: FieldConfig[] = [
        { name: 'functionalRoleIds', label: 'Workflow roles', labelSize: 3},
    ];

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        handleFieldChanged: setFieldValue,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    return (
        <>
            <Container fluid>
                <Row>
                    <Col md='6'>
                        <MultiSelectField {...getFieldProps('functionalRoleIds')} options={functionalRoles} />
                    </Col>
                </Row>
                <Row>
                    <div style={{ marginTop: 15 }}>
                        <DataTable
                            name='dtEmployeeMaintenanceProductCategories'
                            tableData={tableData}
                            colInfos={[
                                { key: true, dataProp: 'id', visible: false },
                                { checkboxButton: { text: 'selected', icon: 'fa-check', color: 'green', onClick: onCheckboxButtonClick } },
                                { title: 'Employee', dataProp: 'employee.name', sort: 'asc' },
                                { title: 'Role', dataProp: 'functionalRole.name' },
                                { dataType: 'bigimage', dataProp: 'productCategory.thumbnailUrl' },
                                { title: 'Name', dataProp: 'productCategory.name' },
                                { title: 'Abbr.', dataProp: 'productCategory.abbreviation' },
                                { title: 'Name tree', dataProp: 'productCategory.nameTree' },
                            ]}
                        />
                    </div>
                </Row>
                {showSpinner && <SpinnerOverlay />}
            </Container>
            <ButtonBar offset={1}>
                <Button type='button' text='Change Employee' onClick={submit} />
            </ButtonBar>
        </>
    );
}
export default EmployeeMaintenance_tab_ProductCategories;